import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Save, 
  Upload, 
  X, 
  ArrowLeft,
  MapPin, 
  Globe, 
  CheckCircle, 
  AlertCircle,
  Camera,
  ImageIcon
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import { uploadToCloudinary } from '../utils/cloudinaryUpload';
import '../styles/GalleryCreatePage.css';

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const GalleryCreatePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Gallery data state
  const [galleryData, setGalleryData] = useState({
    name: '',
    type: '',
    description: '',
    address: '',
    website: '',
    contact_email: '',
    contact_phone: '',
    order_num: 0
  });
  
  // Location data state
  const [locationData, setLocationData] = useState({
    formatted_address: '',
    latitude: null,
    longitude: null,
    geo_point: null
  });
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const [geocodingError, setGeocodingError] = useState(null);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Existing state
  const [coverImage, setCoverImage] = useState(null);
  const [logo, setLogo] = useState(null);
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Image upload states
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  const coverImageRef = React.useRef(null);
  const logoRef = React.useRef(null);

  // Clear geocoding errors when address changes
  useEffect(() => {
    if (geocodingError) {
      setGeocodingError(null);
    }
  }, [galleryData.address]);

  // Gallery types with counts (from your actual data)
  const galleryTypes = [
    { value: 'gallery', label: 'Gallery', count: 278 },
    { value: 'public space', label: 'Public Space', count: 72 },
    { value: 'fair', label: 'Fair', count: 47 },
    { value: 'studio', label: 'Studio', count: 12 },
    { value: 'restaurant/bar', label: 'Restaurant/Bar', count: 11 },
    { value: 'Private Studio', label: 'Private Studio', count: 10 },
    { value: 'event venue', label: 'Event Venue', count: 9 },
    { value: 'online', label: 'Online', count: 1 },
    { value: 'Museum', label: 'Museum', count: 1 },
    { value: 'Pop Up', label: 'Pop Up', count: 1 }
  ];

  // State for keyboard navigation
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);

  // Handle keyboard navigation for suggestions
  const handleKeyDown = (e) => {
    if (!showSuggestions || addressSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < addressSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev > 0 ? prev - 1 : addressSuggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < addressSuggestions.length) {
          const suggestion = addressSuggestions[selectedSuggestionIndex];
          selectSuggestion(suggestion);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
    }
  };

  // Helper function to select a suggestion
  const selectSuggestion = (suggestion) => {
    setGalleryData(prev => ({
      ...prev,
      address: suggestion.formatted_address
    }));
    setLocationData({
      formatted_address: suggestion.formatted_address,
      latitude: suggestion.latitude,
      longitude: suggestion.longitude,
      geo_point: suggestion.geo_point
    });
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    setGeocodingError(null);
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setGalleryData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Trigger geocoding when address changes
    if (field === 'address') {
      // Clear previous error
      setGeocodingError(null);
      
      // Hide suggestions if field is empty
      if (!value.trim()) {
        setShowSuggestions(false);
        setAddressSuggestions([]);
        setLocationData({
          formatted_address: '',
          latitude: null,
          longitude: null,
          geo_point: null
        });
        return;
      }
      
      // Trigger debounced geocoding
      debouncedGeocode(value);
    }
  };

  // Geocoding function
  const geocodeAddress = async (address) => {
    if (!address || address.trim().length < 3) {
      setLocationData({
        formatted_address: '',
        latitude: null,
        longitude: null,
        geo_point: null
      });
      return;
    }

    setGeocodingLoading(true);
    setGeocodingError(null);
    
    try {
      // Use your existing Edge Function
      const { data: authData } = await supabase.auth.getSession();
      const token = authData?.session?.access_token;
      
      const response = await fetch('https://api.vibrantcast.com/functions/v1/google_maps_location', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          input: address,
          user_lat: 41.8781, // Chicago default
          user_lng: -87.6298, // Chicago default
          radius: 50
        })
      });

      if (!response.ok) {
        throw new Error('Failed to geocode address');
      }

      const results = await response.json();
      
      if (results && results.length > 0) {
        // Show all results as suggestions instead of auto-selecting the first one
        setAddressSuggestions(results.slice(0, 5)); // Show top 5 suggestions
        setShowSuggestions(true);
        setSelectedSuggestionIndex(-1); // Reset keyboard navigation
        setGeocodingError(null);
        
        // Don't auto-update the address field - let user choose from suggestions
        console.log('Geocoding successful, showing suggestions:', results.length);
      } else {
        setGeocodingError('No location found for this address');
        setLocationData({
          formatted_address: '',
          latitude: null,
          longitude: null,
          geo_point: null
        });
        setShowSuggestions(false);
        setAddressSuggestions([]);
        setSelectedSuggestionIndex(-1);
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      setGeocodingError('Failed to geocode address. Please try again.');
      setLocationData({
        formatted_address: '',
        latitude: null,
        longitude: null,
        geo_point: null
      });
    } finally {
      setGeocodingLoading(false);
    }
  };

  // Debounced geocoding function - shorter delay for better UX
  const debouncedGeocode = useCallback(
    debounce((address) => {
      geocodeAddress(address);
    }, 300), // Reduced from 500ms to 300ms
    []
  );

  const handleImageUpload = async (file, type) => {
    if (!file) return;

    try {
      if (type === 'cover') {
        setUploadingCover(true);
        const result = await uploadToCloudinary(file, 'image', 'galleries/covers');
        setCoverImageUrl(result.url);
        setCoverImageFile(null);
      } else if (type === 'logo') {
        setUploadingLogo(true);
        const result = await uploadToCloudinary(file, 'image', 'galleries/logos');
        setLogoUrl(result.url);
        setLogoFile(null);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setError(`Failed to upload ${type} image: ${error.message}`);
    } finally {
      if (type === 'cover') setUploadingCover(false);
      if (type === 'logo') setUploadingLogo(false);
    }
  };

  const removeImage = (type) => {
    if (type === 'cover') {
      setCoverImageFile(null);
      setCoverImageUrl('');
    } else if (type === 'logo') {
      setLogoFile(null);
      setLogoUrl('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!galleryData.name) {
      setError('Gallery name is required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Get current user ID
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('galleries')
        .insert([{
          name: galleryData.name,
          type: galleryData.type || null,
          description: galleryData.description || null,
          address: galleryData.address || null,
          website: galleryData.website || null,
          contact_email: galleryData.contact_email || null,
          contact_phone: galleryData.contact_phone || null,
          cover_image: coverImageUrl || null,
          logo: logoUrl || null,
          order_num: galleryData.order_num || 0,
          approved: true, // Auto-approve admin-created galleries
          added_by: user.id,
          
          // Add geo location data
          point_location: locationData.latitude && locationData.longitude 
            ? `${locationData.latitude},${locationData.longitude}` 
            : null,
          geo_point: locationData.geo_point || null
        }])
        .select('*')
        .single();

      if (error) throw error;

      setSuccess('Gallery created successfully!');
      
      // Navigate back after a short delay
      setTimeout(() => {
        navigate('/admin/galleries');
      }, 2000);

    } catch (error) {
      console.error('Error creating gallery:', error);
      setError(`Failed to create gallery: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/galleries');
  };

  return (
    <div className="gallery-create-page">
      {/* Success Message */}
      {success && (
        <div className="success-message">
          {success}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="gallery-form">
        {/* Basic Information */}
        <div className="form-section">
          <h3>Basic Information</h3>
          
          <div className="form-group">
            <label htmlFor="name">Gallery Name *</label>
            <input
              type="text"
              id="name"
              value={galleryData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter gallery name"
              required
              disabled={loading}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="type">Gallery Type</label>
              <select
                id="type"
                value={galleryData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                disabled={loading}
              >
                <option value="">Select Type</option>
                {galleryTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label} ({type.count})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="order_num">Display Order</label>
              <input
                type="number"
                id="order_num"
                value={galleryData.order_num}
                onChange={(e) => handleInputChange('order_num', parseInt(e.target.value) || 0)}
                placeholder="0"
                min="0"
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={galleryData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={4}
              placeholder="Describe the gallery, its mission, and what makes it unique..."
              disabled={loading}
            />
          </div>
        </div>

        {/* Images */}
        <div className="form-section">
          <h3>Images</h3>
          
          <div className="form-row">
            {/* Cover Image */}
            <div className="form-group">
              <label>Cover Image</label>
              <div className="image-upload-container">
                {coverImageUrl ? (
                  <div className="image-preview">
                    <img src={coverImageUrl} alt="Cover preview" />
                    <button
                      type="button"
                      className="remove-image"
                      onClick={() => removeImage('cover')}
                      disabled={loading}
                    >
                      <X size={16} color="#ffffff" />
                    </button>
                  </div>
                ) : (
                  <div className="image-upload-area">
                    <input
                      ref={coverImageRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setCoverImageFile(file);
                          handleImageUpload(file, 'cover');
                        }
                      }}
                      disabled={loading || uploadingCover}
                    />
                    <div className="upload-placeholder">
                      <Camera size={32} color="#6c757d" />
                      <p>Click to upload cover image</p>
                      <span>Recommended: 1200x800px</span>
                    </div>
                  </div>
                )}
                {uploadingCover && (
                  <div className="uploading-indicator">
                    <div className="spinner"></div>
                    Uploading...
                  </div>
                )}
              </div>
            </div>

            {/* Logo */}
            <div className="form-group">
              <label>Logo</label>
              <div className="image-upload-container">
                {logoUrl ? (
                  <div className="image-preview">
                    <img src={logoUrl} alt="Logo preview" />
                    <button
                      type="button"
                      className="remove-image"
                      onClick={() => removeImage('logo')}
                      disabled={loading}
                    >
                      <X size={16} color="#ffffff" />
                    </button>
                  </div>
                ) : (
                  <div className="image-upload-area">
                    <input
                      ref={logoRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setLogoFile(file);
                          handleImageUpload(file, 'logo');
                        }
                      }}
                      disabled={loading || uploadingLogo}
                    />
                    <div className="upload-placeholder">
                      <ImageIcon size={32} color="#6c757d" />
                      <p>Click to upload logo</p>
                      <span>Recommended: 400x400px</span>
                    </div>
                  </div>
                )}
                {uploadingLogo && (
                  <div className="uploading-indicator">
                    <div className="spinner"></div>
                    Uploading...
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Location & Contact */}
        <div className="form-section">
          <h3>Location & Contact</h3>
          
          <div className="form-group">
            <label htmlFor="address">Address</label>
            <div className="address-input-container">
              <input
                type="text"
                id="address"
                value={galleryData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter gallery address (e.g. 123 Main St, Chicago, IL)"
                disabled={loading}
                className={geocodingError ? 'error' : ''}
                autoComplete="off"
              />
              
              {/* Clear button */}
              {galleryData.address && (
                <button
                  type="button"
                  className="clear-button"
                  onClick={() => {
                    setGalleryData(prev => ({ ...prev, address: '' }));
                    setLocationData({
                      formatted_address: '',
                      latitude: null,
                      longitude: null,
                      geo_point: null
                    });
                    setShowSuggestions(false);
                    setAddressSuggestions([]);
                    setGeocodingError(null);
                  }}
                  title="Clear address"
                >
                  <X size={16} />
                </button>
              )}
              
              {/* Loading indicator */}
              {geocodingLoading && (
                <div className="geocoding-status loading">
                  <div className="spinner"></div>
                  <span>Finding location...</span>
                </div>
              )}
              
              {/* Success indicator - show formatted address instead of coordinates */}
              {locationData.formatted_address && !geocodingLoading && !showSuggestions && (
                <div className="geocoding-status success">
                  <CheckCircle size={16} color="#28a745" />
                  <span>Location found: {locationData.formatted_address}</span>
                </div>
              )}
              
              {/* Error indicator */}
              {geocodingError && (
                <div className="geocoding-status error">
                  <AlertCircle size={16} color="#dc3545" />
                  <span>{geocodingError}</span>
                </div>
              )}
              
              {/* Address Suggestions - Show while user is typing */}
              {showSuggestions && addressSuggestions.length > 0 && (
                <div className="address-suggestions">
                  <p className="suggestions-label">Select an address:</p>
                  {addressSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`suggestion-item ${
                        index === selectedSuggestionIndex ? 'selected' : ''
                      }`}
                      onClick={() => selectSuggestion(suggestion)}
                      onMouseEnter={() => setSelectedSuggestionIndex(index)}
                    >
                      <MapPin size={14} className="suggestion-icon" />
                      {suggestion.formatted_address}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Location Preview */}
          {locationData.latitude && (
            <div className="location-preview">
              <h4>Location Details</h4>
              <div className="location-info">
                <div className="location-item">
                  <MapPin size={16} color="#6c757d" />
                  <span>{locationData.formatted_address}</span>
                </div>
                <div className="location-item">
                  <Globe size={16} color="#6c757d" />
                  <span>Coordinates: {locationData.latitude.toFixed(6)}, {locationData.longitude.toFixed(6)}</span>
                </div>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="website">Website</label>
              <input
                type="url"
                id="website"
                value={galleryData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                placeholder="https://example.com"
                disabled={loading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="contact_email">Contact Email</label>
              <input
                type="email"
                id="contact_email"
                value={galleryData.contact_email}
                onChange={(e) => handleInputChange('contact_email', e.target.value)}
                placeholder="contact@gallery.com"
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="contact_phone">Contact Phone</label>
            <input
              type="tel"
              id="contact_phone"
              value={galleryData.contact_phone}
              onChange={(e) => handleInputChange('contact_phone', e.target.value)}
              placeholder="+1 (555) 123-4567"
              disabled={loading}
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleCancel}
            disabled={loading}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Galleries
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading || !galleryData.name}
          >
            {loading ? (
              <>
                <div className="spinner"></div>
                Creating Gallery...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Create Gallery
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default GalleryCreatePage;
